
import * as React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';
import {
    VerificationService,
    ActiveMilitaryPersonalInfoViewModel,
    FieldValidationErrors,
    FormFooterComponent,
    ExtendedFieldValidationErrors,
    ActiveMilitaryPersonalInfoResponse,
    hasFailedInstantMatch,
    Metadata,
    logger,
    VerificationStepsEnum,
    FormSelectChoice,
    MilitaryStatusResponse,
    updateFieldValidationErrorsByFieldId,
    Organization,
    updateViewModelOrganization,
    handleEmailOnKeyDown,
    submitForm,
    isFormFilled,
    resetRefs,
    MilitaryStatusComponent,
    BranchOfServiceComponent,
    FirstNameComponent,
    LastNameComponent,
    BirthDateComponent,
    EmailComponent,
    setRef,
} from '@sheerid/jslib-nightly';
import { LogoComponent } from '../LogoComponent/LogoComponent';


interface StepActiveMilitaryPersonalInfoComponentProps {
    intl: any;
    verificationService: VerificationService;
}

enum MilitaryStatusDefaultMessagesEnum {
    ACTIVE_DUTY = 'Active Duty',
    MILITARY_RETIREE = 'Military Retiree',
    RESERVIST = 'Reservist',
    VETERAN = 'Veteran',
    MILITARY_FAMILY = 'Registered Military Dependent',
}

export const StepActiveMilitaryPersonalInfo = ({ intl, verificationService }: StepActiveMilitaryPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as ActiveMilitaryPersonalInfoViewModel;
    const previousViewModel = verificationService.previousViewModel as ActiveMilitaryPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const verificationResponse = verificationService.verificationResponse as ActiveMilitaryPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);

    const updateActiveMilitaryViewModel = (key: keyof ActiveMilitaryPersonalInfoViewModel, value: string | Metadata) => {
        const nextState: ActiveMilitaryPersonalInfoViewModel = produce(viewModel, (draft: ActiveMilitaryPersonalInfoViewModel) => {
            // @ts-ignore
            draft[key] = value;
        });
        verificationService.updateViewModel(nextState);
    };

    const submitStep = () => {
        const nextState = produce(viewModel, () => {});
        logger.info('StepMilitaryStatusComponent submitting form');
        verificationService.submitStep(
            VerificationStepsEnum.collectMilitaryStatus,
            nextState,
            verificationService.previousVerificationResponse || verificationService.verificationResponse,
        );
    };

    const getAvailableMilitaryStatuses = ():FormSelectChoice[] => {
        const availableStatusesResponse =
            (verificationService.verificationResponse as MilitaryStatusResponse).availableStatuses ||
            (verificationService.previousVerificationResponse as MilitaryStatusResponse).availableStatuses;
        const availableStatuses: FormSelectChoice[] = [];

        for (const status of availableStatusesResponse) {
            availableStatuses.push({
                value: status,
                label: intl.formatMessage({ id: status, defaultMessage: MilitaryStatusDefaultMessagesEnum[status] }),
            });
        }
        return availableStatuses;
    };

    if ((viewModel.status && previousViewModel && previousViewModel.status !== viewModel.status) ||
        (viewModel.status && verificationService.verificationResponse.currentStep === VerificationStepsEnum.collectMilitaryStatus)) {
        submitStep();
    }

    // Refs should be reset to ensure the field order isn't affected by the previous step
    resetRefs();

    return (
        <div id="sid-step-active-military-personal-info" className="sid-l-container">
            { failedInstantMatch ?
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.title" defaultMessage="Check Your Name & Birthdate" />
                    </div>

                    <div className="sid-header__subtitle sid-header__subtitle--error">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.subtitle"
                            defaultMessage="
                                Enter your first and last name on the form as it appears on your military document(s).
                                Keep in mind that may be your given name."
                        />
                        <span className="sid-required-note">
                            <FormattedHTMLMessage id="step.personalInfo.requiredFields" defaultMessage="* Required Fields"/>
                        </span>
                    </div>
                </div>
            :
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title">
                        <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="VERIFY YOUR DETAILS" />
                    </div>

                    <div className="sid-header__subtitle">
                        <FormattedHTMLMessage id="step.personalInfo.subtitle"
                            defaultMessage="Complete the form below to confirm your eligibility." />
                        <span className="sid-required-note">
                            <FormattedHTMLMessage id="step.personalInfo.requiredFields" defaultMessage="* Required Fields"/>
                        </span>
                    </div>
                    <div className="sid-l-space-top-md sid-l-space-btm-lg sid-header__required-fields">
                        <FormattedHTMLMessage id="step.personalInfo.allRequiredFields" defaultMessage="Please fill out all required fields." />
                    </div>
                </div>
            }

            <MilitaryStatusComponent
                value={{
                    value: (verificationService.viewModel as ActiveMilitaryPersonalInfoViewModel).status,
                    label: MilitaryStatusDefaultMessagesEnum[(verificationService.viewModel as ActiveMilitaryPersonalInfoViewModel).status],
                }}
                isErrored={!!fieldValidationErrors.status}
                options={ getAvailableMilitaryStatuses() }
                onChange={
                    (status: FormSelectChoice) => {
                        updateActiveMilitaryViewModel('status', status ? (status.value as string) : '');
                        updateFieldValidationErrorsByFieldId('status', status ? (status.value as string) : '', verificationService);
                    }
                }
            />

            <BranchOfServiceComponent
                autoFocus={viewModel.status && viewModel.organization.id === 0 ? true : false}
                isErrored={!!fieldValidationErrors.organization}
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
                value={(verificationService.viewModel as ActiveMilitaryPersonalInfoViewModel).organization}
                verificationService={verificationService}
            />

            <div className="sid-names">
                <FirstNameComponent

                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    onChange={
                        (newValue: string) => {
                            updateActiveMilitaryViewModel('firstName', newValue);
                            updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                        }
                    }
                />

                <LastNameComponent

                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    onChange={
                        (newValue: string) => {
                            updateActiveMilitaryViewModel('lastName', newValue);
                            updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                        }
                    }
                />
            </div>

            <EmailComponent
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                onChange={
                    (newValue: string) => {
                        updateActiveMilitaryViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
            />

            <BirthDateComponent
                isErrored={!!fieldValidationErrors.birthDate}
                onChange={(newValue: string) => {
                    updateActiveMilitaryViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-btn-collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectActiveMilitaryPersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel) ? 'sid-btn--disabled-like' : ''}`}
                        aria-label="submit"
                        ref={(button: HTMLButtonElement) => setRef('submitButton', button)}
                    >
                        { failedInstantMatch ?
                            <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                        :
                            <FormattedHTMLMessage id="verifyMyMilitaryStatus" defaultMessage="Verify My Military Status" />
                        }
                    </button>
                </div>
            </div>

            <FormFooterComponent verificationService={verificationService}>
                <FormattedHTMLMessage
                    id="militaryInfoShared"
                    defaultMessage="Information entered into this web form will be used for verification purposes. The information will be shared with Wilson."
                />
            </FormFooterComponent>
        </div>
    );
};

export const StepActiveMilitaryPersonalInfoComponent = injectIntl(StepActiveMilitaryPersonalInfo);
